import { z } from 'zod';
import { changeAuthorTypeSchema } from '../../history-management/changeAuthor.js';
import { partnerCockpitLanguageSchema } from '../../partner-cockpit/user-management/auth.js';
import { subscriptionBillingCadenceSchema } from './subscriptionBillingCadence.js';
import { subscriptionOrderSummarySchema } from './subscriptionOrderSummary.js';
import { subscriptionPaymentMethodSchema } from './subscriptionPaymentMethod.js';
import { SubscriptionStatus } from './subscriptionStatus.js';
export const termsAcceptorTypeSchema = z.enum([
    // The mailed checkout, opened from a link sent to the partner contact. Nobody is logged in.
    'CheckoutLink',
    // Self-registration, accepted by the logged-in partner cockpit user.
    'PartnerCockpitUser',
]);
export const TermsAcceptorType = termsAcceptorTypeSchema.enum;
export const termsAcceptorSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(TermsAcceptorType.CheckoutLink),
        // The address the checkout link was mailed to.
        email: z.string(),
        // The partner contact the link was addressed to.
        contactName: z.string().nullable(),
    }),
    z.object({
        type: z.literal(TermsAcceptorType.PartnerCockpitUser),
        // Identifies the account that accepted, the "user ID" the lawyer recommends keeping.
        partnerCockpitUserUuid: z.uuid(),
        email: z.string(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
    }),
]);
export const termsAcceptanceAddressSchema = z.object({
    // Street and number.
    line1: z.string(),
    zip: z.string(),
    city: z.string(),
});
export const termsAcceptancePartnerSchema = z.object({
    // Company name at the moment of acceptance.
    name: z.string(),
    uid: z.string().nullable(),
    // Registered company address, not the billing address.
    address: termsAcceptanceAddressSchema.nullable(),
});
export const termsAcceptanceOrderSchema = subscriptionOrderSummarySchema.omit({ greetingName: true }).extend({
    // Net amounts are on `plan`; this gross one uses the same assumed rate the checkout showed.
    pricePerMonthInclVat: z.string(),
    // The assumed VAT rate behind every incl. VAT amount here, e.g. '8.1'.
    vatPercent: z.string(),
    // Days covered by migrated credits, kept even when the page showed no credit breakdown.
    creditsCoverageDays: z.number().int().min(0),
});
// Stored on the TermsAccepted change event: what the partner was shown and confirmed, frozen at the click.
export const termsAcceptanceRecordSchema = z.object({
    // Printed as the document number, so the proof shows the same reference before and after a validator approves.
    id: z.uuid(),
    // The language the recorded wording was rendered in: the one the page reported, German when it sent none. Null on
    // acceptances recorded before it was stored.
    language: partnerCockpitLanguageSchema.nullable(),
    // The client address of the confirm request; null when the request carried none.
    ipAddress: z.string().nullable(),
    acceptedBy: termsAcceptorSchema,
    partner: termsAcceptancePartnerSchema,
    privacyPolicyVersion: z.string(),
    // The links behind the declaration's link words at that moment.
    termsUrl: z.string(),
    privacyPolicyUrl: z.string(),
    // The checkbox text exactly as rendered, markup stripped. Empty when the wording could not be loaded.
    declaration: z.string(),
    // The notes shown above the checkbox, in display order.
    notes: z.string().array(),
    // The label of the button that submitted the acceptance.
    confirmLabel: z.string(),
    order: termsAcceptanceOrderSchema,
});
export const acceptanceProofCreditConversionSchema = z.object({
    coverageDays: z.number().int().min(0),
    // Null when only the covered days are known, not the conversion behind them.
    credits: z.number().int().min(0).nullable(),
    value: z.string().nullable(),
    // Null when the converted pools were bought at different prices, as on the checkout page.
    pricePerCredit: z.string().nullable(),
});
export const acceptanceProofChangeTypeSchema = z.enum([
    'FreeDays',
    'AgreedStart',
    'CreditsCoverage',
    'Plan',
    'FreeDaysScheduled',
    'FreeTimeAdded',
    'FreeTimeRevoked',
]);
export const AcceptanceProofChangeType = acceptanceProofChangeTypeSchema.enum;
const coveredWindowSchema = z.object({
    days: z.number().int(),
    coveredFrom: z.iso.datetime(),
    coveredUntil: z.iso.datetime(),
});
export const acceptanceProofChangeSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(AcceptanceProofChangeType.FreeDays),
        from: z.number().int(),
        to: z.number().int(),
    }),
    z.object({
        type: z.literal(AcceptanceProofChangeType.AgreedStart),
        from: z.iso.datetime().nullable(),
        to: z.iso.datetime().nullable(),
    }),
    coveredWindowSchema.extend({
        // The window migrated credits actually covered, fixed when the subscription was activated.
        type: z.literal(AcceptanceProofChangeType.CreditsCoverage),
    }),
    z.object({
        type: z.literal(AcceptanceProofChangeType.Plan),
        fromPlanName: z.string(),
        toPlanName: z.string(),
        // Net, like every amount in this domain.
        fromPricePerMonth: z.string(),
        toPricePerMonth: z.string(),
        effectiveFrom: z.iso.datetime(),
    }),
    coveredWindowSchema.extend({
        // The window the free days sold with the subscription open in, after the paid term. Not free time on top.
        type: z.literal(AcceptanceProofChangeType.FreeDaysScheduled),
    }),
    coveredWindowSchema.extend({ type: z.literal(AcceptanceProofChangeType.FreeTimeAdded) }),
    z.object({
        type: z.literal(AcceptanceProofChangeType.FreeTimeRevoked),
        // Null when the revoked free time is not in the subscription's history.
        days: z.number().int().nullable(),
    }),
]);
// A change to the commercial terms made after the acceptance, read from the subscription's history.
export const acceptanceProofChangeEntrySchema = z.object({
    changedAt: z.iso.datetime(),
    // The role, never a name: the proof leaves Houzy.
    changedBy: changeAuthorTypeSchema,
    change: acceptanceProofChangeSchema,
});
export const subscriptionAcceptanceProofSchema = z.object({
    // The record's id, or the TermsAccepted entry's uuid for acceptances without a record.
    documentNumber: z.uuid(),
    // False where the section was rebuilt from today's data because nothing was recorded at the click.
    recordedAtAcceptance: z.object({
        wording: z.boolean(),
        order: z.boolean(),
    }),
    partner: z.object({
        name: z.string(),
        contactName: z.string().nullable(),
        address: termsAcceptanceAddressSchema.nullable(),
        // The address the acceptance came from: the checkout link's recipient or the cockpit user's login.
        email: z.string().nullable(),
        uid: z.string().nullable(),
    }),
    acceptance: z.object({
        acceptedAt: z.iso.datetime(),
        confirmLabel: z.string(),
        ipAddress: z.string().nullable(),
        language: partnerCockpitLanguageSchema.nullable(),
        declaration: z.string(),
        notes: z.string().array(),
        termsVersion: z.string(),
        privacyPolicyVersion: z.string(),
    }),
    order: z.object({
        planName: z.string(),
        commitmentLengthInMonths: z.number().int(),
        billingCadence: subscriptionBillingCadenceSchema,
        paymentMethod: subscriptionPaymentMethodSchema,
        pricePerMonth: z.string(),
        pricePerMonthInclVat: z.string(),
        commitmentTotal: z.string(),
        commitmentTotalInclVat: z.string(),
        vatPercent: z.string(),
        creditConversion: acceptanceProofCreditConversionSchema.nullable(),
        initialFreeDays: z.number().int().min(0),
        agreedStartAt: z.iso.datetime().nullable(),
    }),
    // Empty before approval of a self-registration, when no subscription exists yet to change.
    changesAfterAcceptance: acceptanceProofChangeEntrySchema.array(),
});
// A draft never completed its checkout, so it has nothing to prove even when a confirm was clicked.
export const hasAcceptanceProof = (subscription) => subscription.termsAcceptedAt !== null && subscription.status !== SubscriptionStatus.Draft;
